/**
 * 📊 Normal Sessions Panel
 * 
 * Dashboard de observabilidad para sesiones normales (active, completed, closed, armed_to_close)
 * 
 * Features:
 * - Métricas de salud del sistema (completion rate, avg turns, duration)
 * - Análisis de engagement (help requests, user messages)
 * - Top agentes por uso
 * - Tabla detallada con filtros
 * - Vista de detalles de sesión
 */

import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Clock,
  XCircle,
  Activity,
  TrendingUp,
  Users,
  MessageSquare,
  HelpCircle,
  Eye,
  Filter,
  RefreshCw,
  BarChart3,
  Zap,
  Award,
  ShieldAlert,
  AlertTriangle,
  AlertOctagon,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { getAnalyticsV4Url } from '../config/config';

const NormalSessionsPanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Métricas
  const [metrics, setMetrics] = useState(null);
  
  // Sesiones
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    agentId: 'all',
    status: 'all', // all, active, completed, closed, armed_to_close
    rangeDays: 30, // CAMBIADO: De 7 a 30 días por defecto
    page: 1,
    limit: 20
  });
  
  // Filtro KPI activo (drill-down desde métricas)
  const [activeKpiFilter, setActiveKpiFilter] = useState(null);
  // Valores: null | 'total' | 'exitosas' | 'abandonos' | 'intervenciones'
  
  // Agentes disponibles
  const [agents, setAgents] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  
  // Modal de detalles
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  
  // Selección masiva
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Panel de diagnóstico (contraído por defecto)
  const [isDiagnosticExpanded, setIsDiagnosticExpanded] = useState(false);
  
  // Handler para filtros KPI clickeables
  const handleKpiFilterClick = (filterType) => {
    if (activeKpiFilter === filterType) {
      // Toggle off - desactivar filtro
      setActiveKpiFilter(null);
    } else {
      // Activar nuevo filtro
      setActiveKpiFilter(filterType);
      // Reset a página 1 cuando se aplica filtro
      setFilters(prev => ({ ...prev, page: 1 }));
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [filters.rangeDays, filters.agentId, filters.status, filters.page, filters.limit]);
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseURL = getAnalyticsV4Url();
      const token = localStorage.getItem('token');
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Calcular rango de fechas
      const to = new Date().toISOString();
      const from = new Date(Date.now() - filters.rangeDays * 24 * 60 * 60 * 1000).toISOString();
      
      // 1. Obtener métricas
      const metricsRes = await axios.get(`${baseURL}/analytics/normal/metrics`, {
        params: {
          from,
          to,
          agentId: filters.agentId !== 'all' ? filters.agentId : undefined
        },
        headers
      });
      
      setMetrics(metricsRes.data.metrics);
      
      // 2. Obtener sesiones
      const sessionsRes = await axios.get(`${baseURL}/analytics/normal/sessions`, {
        params: {
          from,
          to,
          agentId: filters.agentId,
          status: filters.status,
          page: filters.page,
          limit: filters.limit
        },
        headers
      });
      
      setSessions(sessionsRes.data.sessions || []);
      setPagination({
        totalCount: sessionsRes.data.pagination?.totalCount || 0,
        totalPages: sessionsRes.data.pagination?.totalPages || 0,
        currentPage: sessionsRes.data.pagination?.page || 1
      });
      
      // 3. Obtener agentes (para filtro)
      const agentsRes = await axios.get(`${baseURL}/analytics/normal/agents`, {
        params: { from, to },
        headers
      });
      
      setAgents(agentsRes.data.agents || []);
      
      // Top 3 agentes por uso
      const topAgentsData = agentsRes.data.agents.slice(0, 3);
      setTopAgents(topAgentsData);
      
    } catch (err) {
      console.error('Error fetching normal sessions data:', err);
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewDetails = async (sessionId) => {
    try {
      const baseURL = getAnalyticsV4Url();
      const token = localStorage.getItem('token');
      
      const res = await axios.get(
        `${baseURL}/analytics/normal/session/${sessionId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setSessionDetails(res.data);
      setSelectedSession(sessionId);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('Error loading session details:', err);
      alert('Error al cargar detalles de la sesión');
    }
  };
  
  const handleSelectSession = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      }
      return [...prev, sessionId];
    });
  };
  
  const handleSelectAll = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map(s => s.sessionId));
    }
  };
  
  const handleDeleteSelected = async () => {
    if (selectedSessions.length === 0) {
      alert('No hay sesiones seleccionadas');
      return;
    }
    
    const confirmMsg = `¿Estás seguro de eliminar ${selectedSessions.length} sesión(es)? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setIsDeleting(true);
    
    try {
      const baseURL = getAnalyticsV4Url();
      const token = localStorage.getItem('token');
      
      // Eliminar cada sesión
      const deletePromises = selectedSessions.map(sessionId =>
        axios.delete(
          `${baseURL}/analytics/normal/session/${sessionId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      );
      
      await Promise.all(deletePromises);
      
      // Limpiar selección y recargar
      setSelectedSessions([]);
      fetchData();
      alert(`${selectedSessions.length} sesión(es) eliminadas exitosamente`);
      
    } catch (err) {
      console.error('Error deleting sessions:', err);
      alert('Error al eliminar sesiones: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsDeleting(false);
    }
  };
  
  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Activa', color: 'bg-blue-100 text-blue-700', icon: Activity },
      completed: { label: 'Completada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      closed: { label: 'Cerrada', color: 'bg-gray-100 text-gray-700', icon: XCircle },
      armed_to_close: { label: 'Por cerrar', color: 'bg-purple-100 text-purple-700', icon: Clock }
    };
    
    const badge = badges[status] || badges.active;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };
  
  const getCategoryBadge = (category, endReason) => {
    const categories = {
      successful: {
        label: 'Exitosa',
        color: 'bg-green-50 border-green-200 text-green-700',
        icon: CheckCircle,
        tooltip: 'Sesión completada con evaluación'
      },
      early_abandon: {
        label: 'Abandono Temprano',
        color: 'bg-gray-50 border-gray-200 text-gray-600',
        icon: XCircle,
        tooltip: 'Usuario salió antes de 3 turnos'
      },
      moderated: {
        label: 'Moderada',
        color: 'bg-orange-50 border-orange-200 text-orange-700',
        icon: ShieldAlert,
        tooltip: 'Cerrada por moderación'
      },
      error: {
        label: 'Error Técnico',
        color: 'bg-red-50 border-red-200 text-red-700',
        icon: AlertTriangle,
        tooltip: 'Fallo del sistema'
      },
      in_progress: {
        label: 'En Curso',
        color: 'bg-blue-50 border-blue-200 text-blue-700',
        icon: Activity,
        tooltip: 'Sesión activa'
      },
      armed_to_close: {
        label: 'Por Evaluar',
        color: 'bg-purple-50 border-purple-200 text-purple-700',
        icon: Clock,
        tooltip: 'Lista para evaluación final'
      }
    };
    
    const cat = categories[category] || categories.in_progress;
    const Icon = cat.icon;
    
    return (
      <span 
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border ${cat.color} text-xs font-medium`}
        title={cat.tooltip}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{cat.label}</span>
      </span>
    );
  };
  
  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-gray-900">Sesiones Normales</h2>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>
      
      {/* PANEL DE DIAGNÓSTICO (Colapsable) */}
      <div className="bg-gray-50 border border-gray-300 rounded-lg overflow-hidden">
        <button
          onClick={() => setIsDiagnosticExpanded(!isDiagnosticExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-gray-600" />
            <span className="font-semibold text-gray-700 text-sm">Panel de Diagnóstico</span>
          </div>
          <div className="flex items-center gap-2">
            {error && <AlertTriangle className="w-4 h-4 text-red-500" />}
            {isDiagnosticExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </div>
        </button>
        
        {isDiagnosticExpanded && (
          <div className="p-4 border-t border-gray-200 space-y-3 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {/* Estado del servicio */}
          <div className="bg-gray-50 rounded p-2 border border-gray-300">
            <div className="font-semibold text-gray-700 mb-1">Servicio</div>
            <div className={`flex items-center gap-1 ${error ? 'text-red-600' : 'text-green-600'}`}>
              {error ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
              {error ? 'ERROR' : 'OK'}
            </div>
          </div>
          
          {/* Conexión DB */}
          <div className="bg-gray-50 rounded p-2 border border-gray-300">
            <div className="font-semibold text-gray-700 mb-1">Base de Datos</div>
            <div className={`flex items-center gap-1 ${metrics ? 'text-green-600' : 'text-gray-400'}`}>
              {metrics ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              {metrics ? 'CONECTADO' : 'Sin datos'}
            </div>
          </div>
          
          {/* Total registros */}
          <div className="bg-gray-50 rounded p-2 border border-gray-300">
            <div className="font-semibold text-gray-700 mb-1">Total Sesiones</div>
            <div className="text-lg font-bold text-gray-900">
              {metrics?.totalSessions ?? '...'}
            </div>
          </div>
          
          {/* Filtros activos */}
          <div className="bg-gray-50 rounded p-2 border border-gray-300">
            <div className="font-semibold text-gray-700 mb-1">Filtros</div>
            <div className="space-y-0.5">
              <div className="text-gray-600">Días: <span className="font-medium">{filters.rangeDays}</span></div>
              <div className="text-gray-600">Agente: <span className="font-medium">{filters.agentId}</span></div>
              <div className="text-gray-600">Estado: <span className="font-medium">{filters.status}</span></div>
            </div>
          </div>
        </div>
        
        {/* Detalles de la query */}
        <div className="bg-gray-50 rounded p-3 border border-gray-300">
          <div className="font-semibold text-gray-700 mb-2 text-xs">Query Backend Esperada:</div>
          <div className="font-mono text-xs text-gray-800 bg-gray-50 p-2 rounded space-y-1">
            <div><span className="text-blue-600">status:</span> &#123; $in: ['active', 'completed', 'closed', 'armed_to_close'] &#125;</div>
            <div><span className="text-blue-600">createdAt:</span> &#123; $gte: {new Date(Date.now() - filters.rangeDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}, $lte: {new Date().toISOString().split('T')[0]} &#125;</div>
            {filters.agentId !== 'all' && (
              <div><span className="text-blue-600">agentId:</span> '{filters.agentId}'</div>
            )}
            {filters.status !== 'all' && (
              <div><span className="text-blue-600">status filter:</span> '{filters.status}'</div>
            )}
          </div>
        </div>
        
        {/* Resumen del problema */}
        {metrics?.totalSessions === 0 && (
          <div className="bg-red-50 border border-red-300 rounded p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertOctagon className="w-4 h-4 text-red-600" />
              <span className="font-bold text-red-900 text-xs">PROBLEMA DETECTADO</span>
            </div>
            <div className="text-xs text-red-800 space-y-1">
              <div>• La query retorna <span className="font-bold">0 registros</span></div>
              <div>• Posibles causas:</div>
              <div className="ml-4">
                - No existen sesiones con status: active, completed, closed, armed_to_close
              </div>
              <div className="ml-4">
                - <span className="font-bold">Las fechas no coinciden con datos reales en MongoDB</span> (MÁS PROBABLE)
              </div>
              <div className="ml-4">
                - El agentId seleccionado no tiene sesiones
              </div>
              <div className="mt-2 font-semibold text-orange-700 bg-orange-100 p-2 rounded">
                SOLUCIÓN: Cambia el rango a "Últimos 30 días" o "Último año" en el filtro de Período
              </div>
            </div>
          </div>
        )}
          </div>
        )}
      </div>
      
      {/* Filtros */}
      <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">Filtros</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Período */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Período
            </label>
            <select
              value={filters.rangeDays}
              onChange={(e) => setFilters({ ...filters, rangeDays: parseInt(e.target.value), page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={1}>Últimas 24 horas</option>
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={60}>Últimos 60 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={365}>Último año</option>
              <option value={9999}>Todos los tiempos</option>
            </select>
          </div>
          
          {/* Agente */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Agente
            </label>
            <select
              value={filters.agentId}
              onChange={(e) => setFilters({ ...filters, agentId: e.target.value, page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos los agentes</option>
              {agents.map(agent => (
                <option key={agent.agentId} value={agent.agentId}>
                  {agent.agentId} ({agent.totalSessions} sesiones)
                </option>
              ))}
            </select>
          </div>
          
          {/* Estado */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activas</option>
              <option value="completed">Completadas</option>
              <option value="closed">Cerradas</option>
              <option value="armed_to_close">Por Evaluar</option>
            </select>
          </div>
          
          {/* Resultados por página */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Por página
            </label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value), page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Métricas principales - Clickeables */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
          {/* Total Sesiones */}
          <div 
            onClick={() => handleKpiFilterClick('total')}
            className={`bg-white p-2.5 md:p-4 rounded-lg border-2 shadow-sm cursor-pointer transition-all duration-150 hover:shadow-md ${
              activeKpiFilter === 'total' 
                ? 'border-blue-500 bg-blue-50' 
                : activeKpiFilter 
                  ? 'border-gray-200 opacity-50' 
                  : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Total</span>
              <BarChart3 className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-xl md:text-3xl font-bold text-gray-900">{metrics.totalSessions}</div>
            <div className="text-xs text-gray-400 mt-0.5 hidden md:block">
              {metrics.activeSessions} activas ahora
            </div>
            {activeKpiFilter === 'total' && (
              <div className="mt-1 text-xs font-medium text-blue-600 flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Todas
              </div>
            )}
          </div>
          
          {/* Tasa de Éxito */}
          <div 
            onClick={() => handleKpiFilterClick('exitosas')}
            className={`bg-white p-2.5 md:p-4 rounded-lg border-2 shadow-sm cursor-pointer transition-all duration-150 hover:shadow-md ${
              activeKpiFilter === 'exitosas' 
                ? 'border-green-500 bg-green-50' 
                : activeKpiFilter 
                  ? 'border-gray-200 opacity-50' 
                  : 'border-gray-200 hover:border-green-300'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Éxito</span>
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
            <div className="text-xl md:text-3xl font-bold text-green-600">{metrics.successRate}%</div>
            <div className="text-xs text-gray-400 mt-0.5 hidden md:block">
              {metrics.completedSuccessful} de {metrics.totalSessions}
            </div>
            {activeKpiFilter === 'exitosas' && (
              <div className="mt-1 text-xs font-medium text-green-600 flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Exitosas
              </div>
            )}
          </div>
          
          {/* Abandonos Tempranos */}
          <div 
            onClick={() => handleKpiFilterClick('abandonos')}
            className={`bg-white p-2.5 md:p-4 rounded-lg border-2 shadow-sm cursor-pointer transition-all duration-150 hover:shadow-md ${
              activeKpiFilter === 'abandonos' 
                ? 'border-gray-500 bg-gray-50' 
                : activeKpiFilter 
                  ? 'border-gray-200 opacity-50' 
                  : 'border-gray-200 hover:border-gray-400'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Abandonos</span>
              <XCircle className="w-4 h-4 text-gray-600" />
            </div>
            <div className="text-xl md:text-3xl font-bold text-gray-900">{metrics.earlyAbandoned}</div>
            <div className="text-xs text-gray-400 mt-0.5 hidden md:block">
              Antes de 3 turnos
            </div>
            {activeKpiFilter === 'abandonos' && (
              <div className="mt-1 text-xs font-medium text-gray-600 flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Filtrado
              </div>
            )}
          </div>
          
          {/* Intervenciones */}
          <div 
            onClick={() => handleKpiFilterClick('intervenciones')}
            className={`bg-white p-2.5 md:p-4 rounded-lg border-2 shadow-sm cursor-pointer transition-all duration-150 hover:shadow-md ${
              activeKpiFilter === 'intervenciones' 
                ? 'border-orange-500 bg-orange-50' 
                : activeKpiFilter 
                  ? 'border-gray-200 opacity-50' 
                  : 'border-gray-200 hover:border-orange-300'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Interv.</span>
              <Activity className="w-4 h-4 text-orange-600" />
            </div>
            <div className="text-xl md:text-3xl font-bold text-orange-600">{metrics.moderationTerminations}</div>
            <div className="text-xs text-gray-400 mt-0.5 hidden md:block">
              Mod. + {metrics.technicalErrors} errores
            </div>
            {activeKpiFilter === 'intervenciones' && (
              <div className="mt-1 text-xs font-medium text-orange-600 flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Filtrado
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Indicador de filtro KPI activo */}
      {activeKpiFilter && activeKpiFilter !== 'total' && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Filtro KPI activo: <span className="font-bold">{activeKpiFilter}</span>
            </span>
          </div>
          <button
            onClick={() => setActiveKpiFilter(null)}
            className="flex items-center gap-1 px-3 py-1 bg-white border border-blue-300 rounded-lg hover:bg-blue-100 transition text-sm font-medium text-blue-700"
          >
            <XCircle className="w-3.5 h-3.5" />
            Limpiar filtro KPI
          </button>
        </div>
      )}
      
      {/* Análisis de Agentes */}
      {topAgents.length > 0 && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-blue-600" />
            Top 3 Agentes con Mayor Tasa de Éxito
          </h3>
          <div className="space-y-3">
            {topAgents.map((agent, idx) => (
              <div key={agent.agentId} className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{agent.agentId}</div>
                    <div className="text-xs text-gray-500">
                      {agent.totalSessions} sesiones · {agent.completedSuccessful} exitosas · {agent.earlyAbandoned} abandonos
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">{agent.successRate}%</div>
                  <div className="text-xs text-gray-500">éxito</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* ⚠️ ELIMINACIÓN MASIVA DESHABILITADA - Prevenir pérdida de datos */}
      {selectedSessions.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg mb-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-700" />
            <span className="text-sm font-bold text-yellow-900">
              ⚠️ Eliminación masiva deshabilitada
            </span>
          </div>
          <p className="text-xs text-yellow-800 mb-3">
            {selectedSessions.length} sesión(es) seleccionada(s). La función de eliminación masiva está temporalmente 
            deshabilitada para prevenir pérdida accidental de datos históricos.
          </p>
          <button
            onClick={() => setSelectedSessions([])}
            className="px-4 py-2 bg-white border border-yellow-400 rounded-lg hover:bg-yellow-100 transition text-sm font-medium"
          >
            Cancelar selección
          </button>
        </div>
      )}
      
      {/* Sesiones */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* ---- MÓVIL: cards ---- */}
        <div className="md:hidden divide-y divide-gray-100">
          {(() => {
            let filteredSessions = sessions;
            if (activeKpiFilter === 'exitosas') filteredSessions = sessions.filter(s => s.status === 'completed' && s.category === 'completed_successful');
            else if (activeKpiFilter === 'abandonos') filteredSessions = sessions.filter(s => s.completedTurns < 3);
            else if (activeKpiFilter === 'intervenciones') filteredSessions = sessions.filter(s => s.endReason === 'moderation_termination' || s.category === 'technical_error');

            if (filteredSessions.length === 0) return (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">
                {activeKpiFilter ? `Sin sesiones: ${activeKpiFilter}` : 'Sin sesiones'}
              </div>
            );

            return filteredSessions.map((session) => (
              <div key={session.sessionId} className="p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {getStatusBadge(session.status)}
                    {getCategoryBadge(session.category, session.endReason)}
                  </div>
                  <button
                    onClick={() => handleViewDetails(session.sessionId)}
                    className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg font-medium hover:bg-blue-700"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Ver
                  </button>
                </div>
                <p className="text-sm font-medium text-gray-900 truncate">{session.userName || 'N/A'}</p>
                <p className="text-xs text-gray-500 truncate mb-2">{session.agentId}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  <span>Turnos: <strong className="text-gray-900">{session.completedTurns}/{session.maxTurns}</strong></span>
                  <span>Dur: <strong>{formatDuration(session.duration)}</strong></span>
                  {session.helpRequestsCount > 0 && <span className="text-orange-600">Ayudas: <strong>{session.helpRequestsCount}</strong></span>}
                  {session.hasEvaluation && <span className="text-green-600">Eval: <strong>{session.overallScore}/10</strong></span>}
                </div>
                <p className="text-xs text-gray-400 mt-1">{formatDate(session.createdAt)}</p>
              </div>
            ));
          })()}
        </div>

        {/* ---- DESKTOP: tabla ---- */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={sessions.length > 0 && selectedSessions.length === sessions.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Resultado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Session ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Agente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Turnos</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Duración</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Ayudas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Evaluación</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(() => {
                let filteredSessions = sessions;
                if (activeKpiFilter === 'exitosas') filteredSessions = sessions.filter(s => s.status === 'completed' && s.category === 'completed_successful');
                else if (activeKpiFilter === 'abandonos') filteredSessions = sessions.filter(s => s.completedTurns < 3);
                else if (activeKpiFilter === 'intervenciones') filteredSessions = sessions.filter(s => s.endReason === 'moderation_termination' || s.category === 'technical_error');

                if (filteredSessions.length === 0) {
                  return (
                    <tr>
                      <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                        {activeKpiFilter ? `No hay sesiones que coincidan con el filtro: ${activeKpiFilter}` : 'No hay sesiones para mostrar'}
                      </td>
                    </tr>
                  );
                }

                return filteredSessions.map((session) => (
                  <tr key={session.sessionId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedSessions.includes(session.sessionId)} onChange={() => handleSelectSession(session.sessionId)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(session.status)}</td>
                    <td className="px-4 py-3">{getCategoryBadge(session.category, session.endReason)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleViewDetails(session.sessionId)} className="text-xs bg-gray-100 hover:bg-blue-100 px-2 py-1 rounded font-mono text-blue-600 hover:text-blue-700 transition-colors">
                        {session.sessionId.substring(0, 20)}...
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{session.userName || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{session.agentId}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(session.createdAt)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{session.completedTurns}/{session.maxTurns}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDuration(session.duration)}</td>
                    <td className="px-4 py-3 text-center">
                      {session.helpRequestsCount > 0 ? (
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-100 text-orange-700 rounded-full font-bold text-sm">{session.helpRequestsCount}</span>
                      ) : <span className="text-gray-400 text-sm">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {session.hasEvaluation ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />{session.overallScore}/10
                        </span>
                      ) : <span className="text-gray-400 text-xs">Sin evaluar</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleViewDetails(session.sessionId)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                        <Eye className="w-4 h-4" />Ver
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
        
        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-3 md:px-6 py-3 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500">
              {sessions.length}/{pagination.totalCount} · p.{pagination.currentPage}/{pagination.totalPages}
            </div>
            
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={filters.page === 1}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Ant</span>
              </button>
              
              <div className="flex items-center gap-1">
                {[...Array(Math.min(pagination.totalPages, 5))].map((_, idx) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) { pageNum = idx + 1; }
                  else if (pagination.currentPage <= 3) { pageNum = idx + 1; }
                  else if (pagination.currentPage >= pagination.totalPages - 2) { pageNum = pagination.totalPages - 4 + idx; }
                  else { pageNum = pagination.currentPage - 2 + idx; }
                  return (
                    <button key={pageNum} onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded ${filters.page === pageNum ? 'bg-blue-600 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'}`}>
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={filters.page >= pagination.totalPages}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal de Detalles (simplificado por ahora) */}
      {showDetailsModal && sessionDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Detalles de Sesión</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Info básica */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">Session ID:</span>
                  <p className="text-sm text-gray-900 mt-1 font-mono">{sessionDetails.session.sessionId.substring(0, 24)}...</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Estado:</span>
                  <p className="mt-1">{getStatusBadge(sessionDetails.session.status)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Agente:</span>
                  <p className="text-sm text-gray-900 mt-1">{sessionDetails.session.agentId}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Usuario:</span>
                  <p className="text-sm text-gray-900 mt-1">{sessionDetails.session.userName}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Turnos:</span>
                  <p className="text-sm text-gray-900 mt-1">
                    {sessionDetails.session.completedTurns}/{sessionDetails.session.maxTurns}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Creada:</span>
                  <p className="text-sm text-gray-900 mt-1">{formatDate(sessionDetails.session.createdAt)}</p>
                </div>
              </div>
              
              {/* Analytics */}
              {sessionDetails.session.analytics && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Estadísticas</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Turnos usuario:</span>
                      <p className="font-semibold">{sessionDetails.session.analytics.userTurnsCount}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Respuestas IA:</span>
                      <p className="font-semibold">{sessionDetails.session.analytics.aiResponsesCount}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Ayudas coach:</span>
                      <p className="font-semibold">{sessionDetails.session.analytics.helpRequestsCount}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Latencia prom:</span>
                      <p className="font-semibold">{sessionDetails.session.analytics.avgLatency || 'N/A'}ms</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Tokens usados:</span>
                      <p className="font-semibold">{sessionDetails.session.analytics.totalTokens}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Moderaciones:</span>
                      <p className="font-semibold">{sessionDetails.session.analytics.moderationsCount}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Historial de Chat */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Timeline Completa de Conversación
                </h4>
                <div className="relative border-l-2 border-gray-300 ml-3 space-y-4 max-h-[500px] overflow-y-auto pr-4">
                  {sessionDetails.events && sessionDetails.events.length > 0 ? (
                    sessionDetails.events
                      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                      .map((event, idx) => {
                        // Helper functions (igual que en Replay)
                        const resolveTurnLabel = (it) => {
                          const t = it?.turn ?? it?.parentTurn;
                          if (t === null || t === undefined) return '—';
                          const n = Number(t);
                          return Number.isFinite(n) ? String(n) : String(t);
                        };
                        
                        let icon, bgColor, borderColor, textColor, title, content;
                        
                        // Determinar tipo de evento
                        if (event.eventType === 'MODERATION') {
                          icon = <ShieldAlert className="w-4 h-4" />;
                          bgColor = 'bg-orange-50';
                          borderColor = 'border-orange-300';
                          textColor = 'text-orange-700';
                          title = `⚠️ MODERACIÓN - ${event.moderation?.severity?.toUpperCase() || 'ALERT'}`;
                          content = (
                            <>
                              <div className="font-medium">{event.moderation?.reason || 'Contenido moderado'}</div>
                              {event.moderation?.inputEvaluated && (
                                <div className="text-xs mt-1 opacity-75">
                                  Input: "{event.moderation.inputEvaluated.substring(0, 80)}..."
                                </div>
                              )}
                            </>
                          );
                        } else if (event.eventType === 'USER_TURN') {
                          icon = '👤';
                          bgColor = 'bg-blue-50';
                          borderColor = 'border-blue-200';
                          textColor = 'text-blue-700';
                          const turnLabel = resolveTurnLabel(event);
                          title = `USUARIO - Turno ${turnLabel}`;
                          
                          // Debug: verificar estructura del evento
                          const userText = event.userInput || event.metadata?.userText || 'Sin contenido disponible';
                          
                          content = (
                            <div className="text-sm whitespace-pre-wrap break-words">
                              {userText}
                            </div>
                          );
                        } else if (event.eventType === 'AI_RESPONSE') {
                          icon = '🤖';
                          bgColor = 'bg-gray-50';
                          borderColor = 'border-gray-200';
                          textColor = 'text-gray-700';
                          const turnLabel = resolveTurnLabel(event);
                          title = `AGENTE - Turno ${turnLabel}`;
                          content = (
                            <div className="text-sm whitespace-pre-wrap break-words">
                              {event.aiResponse || 'Sin respuesta'}
                            </div>
                          );
                        } else if (event.eventType === 'ON_DEMAND_HELP') {
                          icon = <HelpCircle className="w-4 h-4" />;
                          bgColor = 'bg-purple-50';
                          borderColor = 'border-purple-300';
                          textColor = 'text-purple-700';
                          title = '💡 AYUDA COACH';
                          content = (
                            <div className="text-sm">
                              {event.metadata?.helpResponse || 'Ayuda solicitada'}
                            </div>
                          );
                        } else if (event.eventType === 'DECISION') {
                          // 🎯 DECISION events: cambios de fase del state machine
                          const decisionType = event.decision?.type || event.metadata?.decisionType;
                          const isPhaseTransition = decisionType === 'phase_transition';
                          
                          if (isPhaseTransition) {
                            // Cambio de fase del state machine V4
                            icon = <Activity className="w-5 h-5" />;
                            bgColor = 'bg-blue-50';
                            borderColor = 'border-blue-400';
                            textColor = 'text-blue-800';
                            title = '🔄 CAMBIO DE FASE';
                            
                            const phaseBefore = event.systemContext?.phaseBefore || event.metadata?.fromPhase;
                            const phaseAfter = event.systemContext?.phaseAfter || event.metadata?.toPhase;
                            const turnsInPreviousPhase = event.systemContext?.turnsInPhaseBefore;
                            const reason = event.metadata?.reason;
                            
                            content = (
                              <div className="text-sm space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-blue-900">Transición de fase:</span>
                                  <span className="text-xs px-2 py-1 bg-gray-200 text-gray-800 rounded font-medium">
                                    {phaseBefore}
                                  </span>
                                  <span className="text-blue-600 font-bold">→</span>
                                  <span className="text-xs px-2 py-1 bg-blue-200 text-blue-900 rounded font-medium">
                                    {phaseAfter}
                                  </span>
                                </div>
                                {turnsInPreviousPhase !== undefined && (
                                  <div className="text-xs text-gray-600">
                                    Turnos completados en fase anterior: <span className="font-semibold">{turnsInPreviousPhase}</span>
                                  </div>
                                )}
                                {reason && (
                                  <div className="text-xs text-gray-600">
                                    Motivo: <span className="font-medium">{reason === 'max_turns_reached' ? 'Máximo de turnos alcanzado' : reason}</span>
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            // Otras decisiones del sistema
                            icon = <Activity className="w-4 h-4" />;
                            bgColor = 'bg-purple-50';
                            borderColor = 'border-purple-300';
                            textColor = 'text-purple-700';
                            title = `🎯 DECISIÓN: ${decisionType || 'Sistema'}`;
                            content = (
                              <div className="text-xs">
                                {event.decision?.result || 'Decisión del sistema'}
                              </div>
                            );
                          }
                        } else if (event.eventType === 'CONVERSATION_MILESTONE') {
                          icon = <CheckCircle className="w-4 h-4" />;
                          bgColor = 'bg-green-50';
                          borderColor = 'border-green-300';
                          textColor = 'text-green-700';
                          title = `✅ MILESTONE - ${event.milestoneKey || 'Hito'}`;
                          
                          // Mostrar información del hito (NO son cambios de fase)
                          const phaseBefore = event.systemContext?.phase?.before;
                          const phaseAfter = event.systemContext?.phase?.after;
                          const scenarioInfo = event.systemContext?.scenario;
                          
                          content = (
                            <div className="text-sm space-y-1">
                              {phaseBefore && phaseAfter ? (
                                <div>
                                  <span className="font-medium">Transición: </span>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{phaseBefore}</span>
                                  <span className="mx-1">→</span>
                                  <span className="text-xs px-2 py-0.5 bg-green-100 rounded">{phaseAfter}</span>
                                </div>
                              ) : phaseAfter ? (
                                <div>
                                  <span className="font-medium">Fase actual: </span>
                                  <span className="text-xs px-2 py-0.5 bg-green-100 rounded">{phaseAfter}</span>
                                </div>
                              ) : phaseBefore ? (
                                <div>
                                  <span className="font-medium">Desde fase: </span>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{phaseBefore}</span>
                                </div>
                              ) : null}
                              
                              {scenarioInfo?.title && (
                                <div className="text-xs opacity-75">
                                  Escenario: {scenarioInfo.title}
                                  {scenarioInfo.difficulty && ` (${scenarioInfo.difficulty})`}
                                </div>
                              )}
                              
                              {event.metadata?.description && (
                                <div className="text-xs opacity-75">{event.metadata.description}</div>
                              )}
                              
                              {event.metadata?.systemEvent && (
                                <div className="text-xs font-medium text-green-700">
                                  {event.metadata.systemEvent}
                                </div>
                              )}
                              
                              {!phaseBefore && !phaseAfter && !scenarioInfo && !event.metadata?.description && !event.metadata?.systemEvent && (
                                <div className="text-xs opacity-75">
                                  Hito de conversación alcanzado
                                  {event.milestoneKey && <span className="ml-1">({event.milestoneKey})</span>}
                                </div>
                              )}
                            </div>
                          );
                        } else {
                          // Eventos genéricos
                          icon = <Activity className="w-4 h-4" />;
                          bgColor = 'bg-gray-50';
                          borderColor = 'border-gray-300';
                          textColor = 'text-gray-600';
                          title = event.eventType;
                          content = <div className="text-xs">Evento del sistema</div>;
                        }
                        
                        return (
                          <div key={event.eventId || idx} className="relative pl-6 pb-4">
                            <div className={`absolute left-[-1.125rem] top-0 w-6 h-6 rounded-full border-2 ${borderColor} ${bgColor} flex items-center justify-center text-sm`}>
                              {typeof icon === 'string' ? icon : icon}
                            </div>
                            <div className={`${bgColor} border ${borderColor} p-3 rounded-lg`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs font-bold ${textColor}`}>{title}</span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(event.timestamp)}
                                </span>
                              </div>
                              <div className="text-gray-700">{content}</div>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <p className="text-center text-gray-500 text-sm py-4">
                      No hay eventos en esta sesión
                    </p>
                  )}
                </div>
              </div>
              
              {/* Evaluación Final (si existe) */}
              {sessionDetails.session.finalEvaluation && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Evaluación Final
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-700">Puntuación General:</span>
                      <span className="text-2xl font-bold text-green-900">
                        {sessionDetails.session.finalEvaluation.overallScore}/10
                      </span>
                    </div>
                    {sessionDetails.session.finalEvaluation.summary && (
                      <div className="text-sm text-gray-700 mt-3">
                        <strong>Resumen:</strong>
                        <p className="mt-1">{sessionDetails.session.finalEvaluation.summary}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NormalSessionsPanel;
